import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, DollarSign, Clock, Target, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Analytics {
  avg_cycle_time_days: number;
  conversion_rate: number;
  estimate_approval_rate: number;
  revenue: {
    total: number;
    average: number;
    count: number;
  };
  price_per_square_trend: {
    average_per_square: number;
    min: number;
    max: number;
    trend: string;
  };
  period: string;
  period_start: string;
  period_end: string;
}

export function AgentAnalyticsDashboard({ orgId }: { orgId: string }) {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState<string>('monthly');

  useEffect(() => {
    loadAnalytics();
  }, [orgId, timePeriod]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('agent-analytics', {
        body: { org_id: orgId, time_period: timePeriod },
      });

      if (error) throw error;
      setAnalytics(data.analytics);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const MetricCard = ({
    title,
    value,
    description,
    icon: Icon,
    trend,
  }: {
    title: string;
    value: string | number;
    description?: string;
    icon: any;
    trend?: 'up' | 'down' | 'neutral';
  }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs text-muted-foreground">{description}</p>
            {trend && (
              <Badge variant={trend === 'up' ? 'default' : trend === 'down' ? 'destructive' : 'secondary'}>
                {trend === 'up' ? <TrendingUp className="h-3 w-3" /> : trend === 'down' ? <TrendingDown className="h-3 w-3" /> : null}
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Business Analytics</h2>
          <p className="text-muted-foreground">
            AI-powered insights and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timePeriod} onValueChange={setTimePeriod}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={loadAnalytics} disabled={loading} variant="outline" size="icon">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading analytics...</div>
      ) : analytics ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              title="Revenue"
              value={formatCurrency(analytics.revenue.total)}
              description={`${analytics.revenue.count} jobs closed`}
              icon={DollarSign}
              trend="up"
            />
            <MetricCard
              title="Avg Cycle Time"
              value={`${analytics.avg_cycle_time_days} days`}
              description="From lead to close"
              icon={Clock}
              trend={analytics.avg_cycle_time_days < 30 ? 'up' : 'neutral'}
            />
            <MetricCard
              title="Conversion Rate"
              value={`${analytics.conversion_rate}%`}
              description="Leads to jobs"
              icon={Target}
              trend={analytics.conversion_rate > 50 ? 'up' : 'neutral'}
            />
            <MetricCard
              title="Estimate Approval"
              value={`${analytics.estimate_approval_rate}%`}
              description="Approval rate"
              icon={TrendingUp}
              trend={analytics.estimate_approval_rate > 70 ? 'up' : 'neutral'}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Price Per Square Trends</CardTitle>
                <CardDescription>Roofing square pricing analysis</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Average</span>
                  <span className="text-lg font-bold">
                    {formatCurrency(analytics.price_per_square_trend.average_per_square)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Range</span>
                  <span>
                    {formatCurrency(analytics.price_per_square_trend.min)} -{' '}
                    {formatCurrency(analytics.price_per_square_trend.max)}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Revenue Breakdown</CardTitle>
                <CardDescription>Job revenue statistics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Revenue</span>
                  <span className="text-lg font-bold">
                    {formatCurrency(analytics.revenue.total)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Average Per Job</span>
                  <span className="font-medium">
                    {formatCurrency(analytics.revenue.average)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Jobs Closed</span>
                  <span className="font-medium">{analytics.revenue.count}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                AI Insights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {analytics.avg_cycle_time_days > 30 && (
                <p className="text-sm">
                  💡 Your average cycle time is {analytics.avg_cycle_time_days} days. Consider enabling proactive
                  follow-up automation to reduce this.
                </p>
              )}
              {analytics.conversion_rate < 50 && (
                <p className="text-sm">
                  💡 Your conversion rate is {analytics.conversion_rate}%. Focus on faster response times and
                  automated estimate generation.
                </p>
              )}
              {analytics.estimate_approval_rate > 80 && (
                <p className="text-sm">
                  ✨ Excellent! Your {analytics.estimate_approval_rate}% estimate approval rate is above industry
                  average.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">No analytics data available for this period.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
